import { ClickedPointer } from './clicked.js';
import { DefaultPointer } from './default.js';
import { ZoomInPointer } from './zoom-in.js';
import { ZoomOutPointer } from './zoom-out.js';
import type { Coordinate } from '@studio/js-utils/geometry';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';

// Define the type for cursor values
export type PointerState = 'default' | 'zoomIn' | 'zoomOut' | 'click';

// Arrow-tip hotspot per variant, in px from that SVG's own top-left corner. The icon is
// drawn at `-hotspot` so the TIP lands on the pointer: wiring.ts commands the raw pointer
// position, so anchoring anywhere else makes clicks land where the user did not aim.
const Hotspot: Record<PointerState, Coordinate> = {
  click: { x: 10, y: 9 },
  default: { x: 3, y: 2 },
  zoomIn: { x: 14, y: 13 },
  zoomOut: { x: 14, y: 11 }
};

// Define the type for the ref methods
export interface PointerHandle {
  changePointer: (newCursor: PointerState) => void;
  updatePosition: (x: number, y: number) => void;
  showPointer: () => void;
  hidePointer: () => void;
}

// Define the Cursor component
export const Pointer = forwardRef<PointerHandle>((_props, ref) => {
  const [type, setType] = useState<PointerState>('default'); // Initial state
  const divRef = useRef<HTMLDivElement>(null);

  const positionRef = useRef({ x: 0, y: 0 });
  useImperativeHandle(ref, () => ({
    changePointer(newCursor) {
      setType(newCursor);
    },
    showPointer() {
      divRef.current?.style?.setProperty?.('display', 'block');
    },
    hidePointer() {
      divRef.current?.style?.setProperty?.('display', 'none');
    },
    updatePosition(nextX, nextY) {
      const { x, y } = positionRef.current;

      if (x === nextX && y === nextY) {
        return;
      }

      positionRef.current = { x: nextX, y: nextY };

      if (divRef.current) {
        divRef.current.style.transform = `translate(${nextX}px, ${nextY}px)`;
      }
    }
  }));

  const cursor = useMemo(() => {
    switch (type) {
      case 'click':
        return <ClickedPointer />;
      case 'zoomIn':
        return <ZoomInPointer />;
      case 'zoomOut':
        return <ZoomOutPointer />;
    }

    return <DefaultPointer />;
  }, [type]);

  const hotspot = Hotspot[type];

  return (
    // The Pointer is a purely-visual custom cursor for the Zoom (Move)
    // tool — it follows the real pointer position via JS-driven
    // `transform: translate(...)`. The visual SVG extends up-and-left
    // from the pointer tip, so as the user approaches the top-right
    // `<NeedsAttentionChip>` (`right-8 top-8` in `annotation.tsx`) the
    // SVG's bounding box overlaps the chip area before the cursor
    // does. With default `pointer-events: auto`, the SVG element wins
    // hit-testing against the chip in that moment (browser hit-tests
    // the painted SVG geometry, not the 0×0 wrapper div), and the
    // chip click is swallowed. Force `pointer-events: none` on the
    // entire Pointer subtree — there is no behavior on the cursor
    // SVG to lose. Other tools (Center, FastFrame, Joystick) draw
    // their cursor INTO the `<Canvas>` itself via `onDraw`, so they
    // have no separate hit-testable SVG and were not affected by the
    // same race after the `z-40` chip wrapper fix. See
    // `annotation.tsx` for the wrapper-side half of the contract.
    <div className='pointer-events-none absolute left-0 top-0' ref={divRef}>
      {/* Never anchor a box corner — that offsets the tip by a whole icon (~30px). */}
      <span
        className='absolute left-0 top-0'
        style={{ transform: `translate(${-hotspot.x}px, ${-hotspot.y}px)` }}
      >
        {cursor}
      </span>
    </div>
  );
});
